import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import NoMatch from '../NoMatch';
import { useHasUserFlag } from '../../hooks';
import { getEventTypes } from '../../selectors/event_types';
import { getItems } from '../../selectors';
import { PresentationStyle, PresentationStyleType } from '../project/StageFormFields/presentation/types';
import PresentationPreviewPane from './PresentationPreviewPane';
import PresentationSkubotPanel from './PresentationSkubotPanel';
import { buildPresentationChatContext } from './buildPresentationChatContext';
import { skubotApi } from '../skubot/lib/skubotApi';
import { createNewConversation, selectActiveConversation } from '../../redux/presentationBuilderChat';
import { createLoadOrder } from '../../actions/order';
import { Modal, ModalBackdrop, ModalBody, ModalHeader, ModalContent, ModalPositioner, ModalTitle, CloseButton } from '@commonsku/design-system';

export const PRESENTATION_BUILDER_AGENT_FLAG = 'PRESENTATION-BUILDER-AGENT';
export const SKUBOT_ENABLED = 'SKUBOT_ENABLED';

const PageRoot = styled.div`
  &&&&& {
    display: flex;
    width: 100%;
    height: calc(100vh - 96px);
    overflow: hidden;
    background: #fafafa;
    margin-top: 0;
    padding-left: 0;
  }

  @media screen and (max-width: 39.9375em) {
    &&&&& {
      padding-left: 0;
    }
  }
`;

interface PresentationBuilderPageProps {
  isPresentationBuilderOpen: boolean;
  project: {
    job_number: string;
    event_type_id?: string;
    total_budget?: string | number | null;
  };
  order: {
    order_id: string;
    form_number: string;
    locked?: number | string;
    tenant_id?: string;
    client_name?: string;
  };
  presentationStyle: PresentationStyleType;
}

export default function PresentationBuilderPage({
  order,
  project,
  presentationStyle,
  isPresentationBuilderOpen: initialIsPresentationBuilderOpen = false,
}: PresentationBuilderPageProps) {
  const dispatch = useDispatch();
  const activeConversation = useSelector(selectActiveConversation);
  const hasBuilderFlags = useHasUserFlag([PRESENTATION_BUILDER_AGENT_FLAG, SKUBOT_ENABLED]);
  const [isPresentationBuilderOpen, setIsPresentationBuilderOpen] = useState(initialIsPresentationBuilderOpen);
  const location = useLocation();
  const navigate = useNavigate();
  const pageRootRef = useRef<HTMLDivElement>(null);
  const isChatDisabled = Number(order.locked) === 1;
  const eventTypes = useSelector(getEventTypes);
  const items = useSelector(getItems) as Record<string, {
    order_id?: string;
    item_name?: string;
    parent_type?: string;
    option_item_id?: string | null;
  }> | null | undefined;
  const orderItems = useMemo(
    () => Object.values(items ?? {}).filter((item) => item.order_id === order.order_id),
    [items, order.order_id]
  );

  const chatContext = useMemo(
    () => buildPresentationChatContext({ order, project, eventTypes, items: orderItems }),
    [order, project, eventTypes, orderItems]
  );

  useEffect(() => {
    dispatch(createLoadOrder(order.order_id, true));
  }, [dispatch, order.order_id]);

  useEffect(() => {
    skubotApi.getSession();
  }, []);

  useEffect(() => {
    if (!activeConversation?.sessionId) {
      dispatch(createNewConversation());
    }
  }, [activeConversation?.sessionId, dispatch]);

  if (!(hasBuilderFlags && presentationStyle === PresentationStyle.TEMPLATE)) {
    return <NoMatch location={location} />;
  }

  return (
    <Modal
      size="full"
      open={isPresentationBuilderOpen}
    >
      <div className="presentation-builder-modal">
        <ModalBackdrop>
          <ModalPositioner>
            <ModalContent backgroundColor="#FAFAFA">
              <ModalHeader backgroundColor="white">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <div>
                    <ModalTitle style={{ margin: 0, }}>Build with skubot</ModalTitle>
                  </div>
                  <div>
                    <CloseButton onClick={() => {
                      setIsPresentationBuilderOpen(false);
                      navigate((location.pathname.endsWith('/') ? location.pathname.slice(0, -1) : location.pathname).replace('/builder', ''));
                      // dispatch(createLoadOrder(order.order_id, true));
                    }} />
                  </div>
                </div>
              </ModalHeader>
              <ModalBody backgroundColor="#FAFAFA" paddingBottom="0">
                <PageRoot ref={pageRootRef} className="main-content presentation-builder-main-content">
                  <PresentationPreviewPane
                    orderId={order.order_id}
                    title={`Presentation #${order.form_number}`}
                  />
                  <PresentationSkubotPanel disabled={isChatDisabled} chatContext={chatContext} />
                </PageRoot>
              </ModalBody>
            </ModalContent>
          </ModalPositioner>
        </ModalBackdrop>
      </div>
    </Modal>
  );
}
